import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SocialLinks from "../components/site/SocialLinks";
import GuestbookWidget from "../components/site/GuestbookWidget";

const THOMAS_HERO =
    "https://customer-assets.emergentagent.com/job_twin-keys-1/artifacts/fsfwyv3r_Thomas%20Rabitsch%20Startseite.jpeg";
const BERNHARD_HERO =
    "https://customer-assets.emergentagent.com/job_twin-keys-1/artifacts/52onw45d_Bernhard%20Rabitsch%20Startseite.jpeg";

const Half = ({ side, to, hero, name, eyebrow, description, accent, testid }) => {
    return (
        <Link
            to={to}
            data-side={side}
            data-testid={testid}
            className="split-side group flex-1 min-h-[58vh] md:min-h-screen relative"
        >
            <div className="split-bg" style={{ backgroundImage: `url(${hero})` }} />
            <div className="split-overlay" />

            <div className="relative z-10 h-full min-h-[58vh] md:min-h-screen flex flex-col justify-end p-8 md:p-14 lg:p-20">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: side === "left" ? 0.2 : 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div
                        className="text-xs md:text-sm uppercase tracking-[0.32em] mb-4"
                        style={{ color: accent }}
                    >
                        {eyebrow}
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl lg:text-8xl font-bold leading-[0.95] tracking-tight text-white">
                        {name.split(" ").map((part, i) => (
                            <span key={i} className="block">
                                {part}
                            </span>
                        ))}
                    </h1>
                    <p className="mt-6 max-w-md text-zinc-300/90 text-base md:text-lg leading-relaxed">
                        {description}
                    </p>
                    <div className="mt-8 inline-flex items-center gap-3 text-white border-b border-transparent group-hover:border-white pb-1 transition-all">
                        <span className="uppercase tracking-[0.28em] text-sm">Entdecken</span>
                        <ArrowUpRight
                            className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                            strokeWidth={1.5}
                        />
                    </div>
                </motion.div>
            </div>
        </Link>
    );
};

export default function Landing() {
    return (
        <div className="relative w-full min-h-screen bg-black overflow-hidden" data-testid="landing-root">
            <GuestbookWidget className="absolute top-5 right-5 md:top-6 md:right-8 z-50" />

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 1 }}
                className="absolute top-6 left-0 right-0 z-40 flex flex-col items-center text-zinc-400 text-xs tracking-[0.4em] uppercase pointer-events-none"
            >
                <div className="font-display text-white text-base md:text-lg tracking-[0.4em] normal-case">
                    rabitsch<span className="text-[var(--rb-primary)]">.</span>supporter
                </div>
                <div className="mt-1 text-[10px] md:text-xs text-zinc-500">
                    Eine kleine musikalische Reise
                </div>
            </motion.div>

            <div className="flex flex-col md:flex-row min-h-screen">
                <Half
                    side="left"
                    to="/thomas"
                    hero={THOMAS_HERO}
                    name="Thomas Rabitsch"
                    eyebrow="Musiker · Produzent · Komponist"
                    description="Eine prägende Figur der österreichischen Musikszene."
                    accent="var(--rb-primary)"
                    testid="landing-thomas"
                />
                <Half
                    side="right"
                    to="/bernhard"
                    hero={BERNHARD_HERO}
                    name="Bernhard Rabitsch"
                    eyebrow="Multi-Instrumentalist · Sänger · Showmaster"
                    description="Ein Virtuose mit großem Charisma."
                    accent="var(--rb-secondary)"
                    testid="landing-bernhard"
                />
            </div>

            <div className="split-divider" />

            <div className="absolute bottom-6 left-6 md:left-10 z-40">
                <SocialLinks />
            </div>

            <div className="absolute bottom-8 inset-x-0 z-40 flex justify-center pointer-events-none">
                <div className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-zinc-400 scroll-hint text-center">
                    Wähle eine Seite
                </div>
            </div>
        </div>
    );
}
